import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * /app/account — signed-in user changes their own password.
 * Requires current password to defeat session hijack attempts.
 * Doubles as the FORCED password change surface when the user was created
 * by an Admin or had their password reset — the sidebar is hidden in
 * that mode and this is the only place they can go.
 */
export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refreshUser();
      setDone(true);
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password updated");
      if (forced) {
        setTimeout(() => navigate("/app", { replace: true }), 1200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div>
        <p className="overline text-primary">Your account</p>
        <h1 className="font-dashboard text-3xl font-bold tracking-tight">Change password</h1>
        <p className="mt-1 text-sm text-muted-foreground">Signed in as <b>{user?.email}</b>.</p>
      </div>

      {forced && !done && (
        <Card className="border-primary/40 bg-primary/5 p-4" data-testid="force-pw-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="font-dashboard text-sm font-semibold text-primary">One-time password change required</div>
              <p className="mt-1 text-xs text-muted-foreground">
                You're signing in with a temporary password set by an Admin. Please pick your own before you continue —
                you won't be able to reach the rest of the portal until this is done.
              </p>
            </div>
          </div>
        </Card>
      )}

      {done ? (
        <Card className="border-primary/30 bg-primary/5 p-8 text-center" data-testid="pw-changed-card">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-primary" />
          <h2 className="font-dashboard text-xl font-bold">Password updated</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {forced ? "Taking you to your dashboard…" : "You'll use the new password next time you sign in."}
          </p>
          {!forced && (
            <Button onClick={() => setDone(false)} variant="outline" className="mt-6" data-testid="change-again-btn">Change again</Button>
          )}
        </Card>
      ) : (
        <Card className="border-border bg-white p-8">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="cur">Current password</Label>
              <Input id="cur" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="cur-pw-input" />
            </div>
            <div>
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="new-pw-input" />
              <p className="mt-1 text-[11px] text-muted-foreground">At least 8 characters.</p>
            </div>
            <div>
              <Label htmlFor="conf">Confirm new password</Label>
              <Input id="conf" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-pw-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="change-pw-submit">
              <KeyRound className="h-4 w-4" /> {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
